import { ApiError } from './ApiError.js';

/*
 * The API's base URL. Vite inlines this at build time, so it has to be present
 * when the bundle is built, not when it runs — a missing value would otherwise
 * surface as requests to the site's own origin, which 404 confusingly.
 */
const BASE_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

if (!BASE_URL && import.meta.env.PROD) {
  // Loud, but at build/boot rather than on a parent's first form submission.
  console.error('VITE_API_URL is not set. The production build cannot reach the API.');
}

/*
 * The access token lives in a module variable rather than localStorage. It is
 * short-lived (15 minutes) and the refresh token that renews it is in an
 * httpOnly cookie, so a script injected into the page has nothing durable to
 * steal. The cost is that a hard refresh needs one silent /auth/refresh call —
 * AuthProvider does that on mount.
 */
let accessToken = null;
let onUnauthorized = null;

export function setAccessToken(token) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

/** Lets AuthProvider react to a session that has ended server-side. */
export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

/*
 * Concurrent 401s must not each fire their own refresh — five parallel queries
 * would rotate the refresh token five times and invalidate each other. The
 * first one starts the refresh; the rest await the same promise.
 */
let refreshInFlight = null;

async function refreshAccessToken() {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) return null;

      const body = await response.json();
      accessToken = body?.data?.accessToken ?? null;
      return accessToken;
    } catch {
      return null;
    } finally {
      // Cleared on the next tick so callers awaiting this promise all see it.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

async function parse(response) {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    // A proxy error page or a gateway timeout — not our envelope.
    throw new ApiError('The server sent a response we could not read.', {
      status: response.status,
    });
  }
}

function toApiError(body, status) {
  return new ApiError(body?.message ?? 'Request failed', {
    status,
    fieldErrors: body?.errors ?? [],
    requestId: body?.requestId ?? null,
  });
}

/**
 * Every call goes through here. Unwraps the `{ success, message, data }`
 * envelope and returns `data`, so callers never see the wrapper.
 *
 * @param {string} path        path under the API root, e.g. '/enquiries'
 * @param {object} [options]
 * @param {boolean} [options.auth]  attach the access token, refreshing once on 401
 */
export async function request(path, { auth = false, retryOn401 = true, ...init } = {}) {
  const headers = new Headers(init.headers);

  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (auth && accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  let response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers,
      // Needed for the refresh cookie; harmless on the public endpoints.
      credentials: 'include',
    });
  } catch {
    // fetch only rejects on a genuine network failure, never on a 4xx/5xx.
    throw new ApiError('Network request failed', { status: 0 });
  }

  /*
   * One transparent retry: the access token expired mid-session, so renew it
   * and replay. `retryOn401` stops this recursing if the replay 401s too.
   */
  if (response.status === 401 && auth && retryOn401) {
    const renewed = await refreshAccessToken();

    if (renewed) {
      return request(path, { ...init, auth, retryOn401: false });
    }

    accessToken = null;
    onUnauthorized?.();
  }

  const body = await parse(response);

  if (!response.ok) throw toApiError(body, response.status);

  return body?.data ?? null;
}

export const api = {
  get: (path, options) => request(path, { ...options, method: 'GET' }),
  post: (path, payload, options) =>
    request(path, {
      ...options,
      method: 'POST',
      body: payload === undefined ? undefined : JSON.stringify(payload),
    }),
  patch: (path, payload, options) =>
    request(path, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
};

export { refreshAccessToken, BASE_URL };
